/**
 * Trang "Rải link lụm tiền": dán link Shopee/ShopeeFood/Lazada/TikTok Shop →
 * nhận link đã gắn mã hoàn tiền của user.
 *
 * Guest dùng được cho Shopee/ShopeeFood/Lazada; riêng TikTok Shop chỉ xem được
 * hoa hồng rồi mới mời đăng nhập (xem useLinkConverter.js).
 */
import React, { useCallback } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import Header from "../../components/Header";
import FooterV2 from "../../components/FooterV2";
import AffiliateFraudPolicyNote from "../../components/AffiliateFraudPolicyNote";
import { detectPlatform } from "./detect-platform";
import { useLinkConverter } from "./useLinkConverter";
import { LinkConverterHero, LinkConverterGuide } from "./LinkConverterGuide";
import LinkConverterInput from "./LinkConverterInput";
import LinkConverterResult from "./LinkConverterResult";
import LinkConverterProductPreview from "./LinkConverterProductPreview";

function LinkConverter() {
    const {
        inputLink, onChangeLink, clearLink,
        resultLink, resultPlatform, preview, previewPlatform, loading, error,
        convert, isLogined, userInfo,
    } = useLinkConverter();

    // Đi qua onChangeLink để dùng chung logic bóc link khỏi đoạn text + auto tạo link.
    const handlePaste = useCallback(async () => {
        try {
            const text = await navigator.clipboard.readText();
            if (text) onChangeLink(text);
        } catch {
            // Clipboard API không khả dụng (trình duyệt cũ / chưa cấp quyền)
        }
    }, [onChangeLink]);

    return (
        <div className="min-h-dvh flex flex-col bg-surface-sunken">
            <Header />
            <div className="flex-1 pt-16 pb-20 md:w-[768px] lg:w-[1024px] md:mx-auto w-full mt-4">
                <LinkConverterHero />

                <div className="bg-surface p-4">
                    {isLogined ? (
                        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-xl flex items-center gap-2">
                            <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0">
                                {(userInfo?.name?.charAt(0) ?? "B").toUpperCase()}
                            </div>
                            <div className="text-sm">
                                <p className="font-semibold text-green-800">{userInfo?.name ?? "Bạn"}</p>
                                <p className="text-green-600 text-xs">
                                    {userInfo?.userId
                                        ? `Link sẽ tự gắn ID #${userInfo.userId} của bạn`
                                        : "Link sẽ tự gắn ID của bạn"}
                                </p>
                            </div>
                        </div>
                    ) : (
                        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-2">
                            <FontAwesomeIcon icon={faExclamationTriangle} className="text-amber-500" />
                            <div className="text-sm flex-1">
                                <p className="font-semibold text-amber-700">Bạn chưa đăng nhập</p>
                                <p className="text-amber-600 text-xs">
                                    Đăng nhập để link tự gắn ID của bạn và nhận hoa hồng.
                                </p>
                            </div>
                            <Link
                                to="/login?redirect=link-converter"
                                className="text-xs font-semibold text-white bg-amber-500 px-3 py-1.5 rounded-full shrink-0 hover:bg-amber-600"
                            >
                                Đăng nhập
                            </Link>
                        </div>
                    )}

                    <LinkConverterInput
                        value={inputLink}
                        platform={detectPlatform(inputLink)}
                        loading={loading}
                        onChange={onChangeLink}
                        onSubmit={convert}
                        onPaste={handlePaste}
                        onClear={clearLink}
                    />

                    {/* Điều khoản chống gian lận TTLK — ngay dưới nút tạo link */}
                    <AffiliateFraudPolicyNote className="mt-3" />

                    {error && (
                        <p className="mt-3 p-3 text-sm text-red-600 text-center bg-red-50 border border-red-200 rounded-xl">
                            {error}
                        </p>
                    )}

                    {/* Hoa hồng của sản phẩm vừa dán. Guest + sàn cần đăng nhập (TikTok)
                        thì card này là kết quả duy nhất nên kèm luôn nút đăng nhập. */}
                    {preview && previewPlatform && (
                        <LinkConverterProductPreview
                            product={preview}
                            platform={previewPlatform}
                            lockedForGuest={!isLogined && !resultLink}
                        />
                    )}

                    {resultLink && <LinkConverterResult link={resultLink} platform={resultPlatform} />}
                </div>

                <LinkConverterGuide />

                <div className="mt-2" />
                <FooterV2 />
            </div>
        </div>
    );
}

export default LinkConverter;
